import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { SEARCH_TERM_SECTIONS } from "@/lib/search-term-sections";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/roof-replacement-phoenix";
const NAME = "Roof Replacement";
const TITLE = "Roof Replacement in Phoenix, AZ | Durafoam Roofing";
const DESCRIPTION =
  "Phoenix roof replacement done right — foam, tile, shingle, and flat systems, plus foaming over an existing roof without a full tear-off. Family-owned since 1989, ROC223900.";

const faqs = [
  {
    q: "How long does a roof replacement take in Phoenix?",
    a: "Most residential re-roofs run two to five working days. A foam application over an existing flat roof is often one to two days; a full tile tear-off and re-lay on a larger home can run a week. Weather during monsoon season is the main variable.",
  },
  {
    q: "Do you always have to tear the old roof off?",
    a: "No. On many flat and low-slope roofs — built-up tar and gravel, torch-down, TPO, some metal — we can prep the existing surface and spray foam directly over it. That saves the cost and mess of a tear-off. Saturated insulation, loose gravel, or a failing deck have to come off first.",
  },
  {
    q: "Can you foam over my existing flat roof?",
    a: "Usually yes, if the roof is dry and structurally sound. We core-test or probe suspect areas during the free inspection to confirm the substrate isn't holding moisture. Wet areas get cut out and rebuilt; the rest gets foamed over.",
  },
  {
    q: "Tile or foam — which is better for a Phoenix home?",
    a: "It depends on the roof, not on preference. Sloped sections shed water and belong under tile or shingle. Flat and low-slope sections need a monolithic, seamless system, and that's where spray foam outperforms everything else in the Valley. Most Phoenix homes end up with both.",
  },
  {
    q: "What warranty comes with a new roof?",
    a: "Foam systems carry a 5-year or 10-year warranty depending on the coating thickness you choose. After that warranty period we recommend recoating every 5 years, which keeps the warranty in force and protects the foam underneath — the foam itself routinely lasts 30+ years on that schedule. Tile and shingle re-roofs carry manufacturer material warranties plus our workmanship warranty.",
  },
  {
    q: "Will insurance pay for a roof replacement?",
    a: "Insurance covers sudden damage — monsoon wind, hail, a fallen tree — not wear-out from age. If a storm damaged your roof we'll document the damage during the inspection so you can file with confidence. Age-related replacement is an out-of-pocket project, but give us a call and we can talk through solutions.",
  },
];

export const Route = createFileRoute("/roof-replacement-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({
      override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: NAME,
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="roof-replacement-phoenix"
      eyebrow="Roof Replacement"
      h1="Roof Replacement in Phoenix"
      subhead="Foam, tile, shingle, and flat roof systems — installed by a family-owned Phoenix roofer that's been doing this since 1989. Licensed ROC223900."
      heroBullets={[
        "Free on-site inspection before any scope",
        "Foam, tile, shingle, and commercial flat systems",
        "Foam over many existing roofs — no full tear-off",
        "Written, line-item estimates and real warranties",
      ]}
      whatIs={{
        heading: "Repair it or replace it?",
        body: (
          <>
            <p>
              Most roofs give you warning before they're finished. The honest answer on repair
              versus replacement comes from what's happening under the surface, not from what the
              roof looks like from the driveway — and that's what the free inspection is for.
            </p>
            <p>Replacement is usually the right call when you're seeing:</p>
            <ul>
              <li>
                <strong>Leaks in multiple areas.</strong> One isolated leak can often be repaired.
                If we're finding leaks in several unrelated areas, widespread cracking, saturated
                roofing, or a history of repeated repairs, that's when we start looking at whether
                the roofing system itself is reaching the end of its useful life.
              </li>
              <li>
                <strong>A soft or saturated deck.</strong> Localized wet material can usually be
                cut out and rebuilt. Widespread saturation or deterioration of the sheathing or
                insulation is where a tear-off becomes much more likely — patching over water that
                has spread across the deck only traps it.
              </li>
              <li>
                <strong>Failed underlayment.</strong> On tile roofs the tile is the rain shield but
                the underlayment is the waterproofing. When 20-year felt is brittle, the roof needs
                a remove-and-replace even if the tile looks perfect.
              </li>
              <li>
                <strong>Age plus repair history.</strong> If you're spending money every monsoon on
                a roof that's near the end of its rated life, that money is better applied to the
                replacement.
              </li>
            </ul>
            <p>
              If it's genuinely repairable, we'll tell you that. See{" "}
              <Link to="/roof-repair">roof repair</Link> and{" "}
              <Link to="/roof-leak-repair">roof leak repair</Link> for the repair side of the
              business.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "Choose your roof type",
        body: (
          <>
            <p>
              We install and replace every residential and commercial roof type common in the
              Valley. Here's where each one fits:
            </p>
            <ul>
              <li>
                <strong>
                  <Link to="/foam-roofing">Spray foam roofing</Link>
                </strong>{" "}
                — our preferred roofing system for most Phoenix flat and low-slope roofs. Foam
                creates a seamless, fully adhered roofing surface and adds insulation above the roof
                deck, which can reduce heat transfer into the building. When the underlying foam
                remains in good condition, the protective coating can be renewed rather than
                replacing the entire roofing system.
              </li>
              <li>
                <strong>
                  <Link to="/foam-roof-coating-recoating">Foam recoating</Link>
                </strong>{" "}
                — if you already have foam, you may not need a replacement at all. After the initial
                install warranty, we recommend a fresh coating every 5 years in Phoenix — a fraction
                of the cost of a new roof, and it keeps your warranty in force.
              </li>
              <li>
                <strong>
                  <Link to="/tile-roofing">Tile roofing</Link>
                </strong>{" "}
                — concrete and clay tile on sloped sections. Most Phoenix "tile replacements" are
                actually a remove-and-replace: we lift and stack your existing tile, install new
                underlayment, and re-lay the tile.
              </li>
              <li>
                <strong>
                  <Link to="/shingle-roofing">Shingle roofing</Link>
                </strong>{" "}
                — architectural and Class 4 impact-rated asphalt shingles for steeper-pitched roofs
                where tile isn't the right fit.
              </li>
              <li>
                <strong>
                  <Link to="/commercial-roofing">Commercial and flat roofing</Link>
                </strong>{" "}
                — foam, coatings, and repairs on warehouses, retail, apartments, and HOA-managed
                buildings.
              </li>
            </ul>
          </>
        ),
      }}
      extraSection={{
        heading: "Before we recommend foam, tile, or shingles",
        body: (
          <>
            <p>
              We don't recommend a roof based on appearance alone. We look at the slope of the roof,
              what's already installed, how many existing layers there are, the condition of the
              deck, drainage, structural weight, penetrations, insulation needs, and budget.
            </p>
            <p>
              On flat and low-slope roofs, foam is usually our first choice. On pitched roofs,
              shingles or an existing tile system may make more sense.
            </p>
            <p>
              If a home doesn't already have tile, we generally don't recommend adding a heavy tile
              system without first verifying that the structure was designed for the additional
              weight.
            </p>
            <p>
              On an existing tile roof, we often remove and reset the original tiles while replacing
              the underlayment underneath. Before starting, we also verify that replacement tiles are
              available in case some break or need to be matched.
            </p>
          </>
        ),
      }}
      extraSections={[
        {
        heading: "Other roof types we work with",
        body: (
          <>
            <p>
              Plenty of Phoenix buildings still carry older flat-roof systems. We work on all of
              them, and in most cases we can replace them with foam without a full tear-off:
            </p>
            <ul>
              <li>
                <strong>Built-up roofing (tar and gravel):</strong> layers of felt and asphalt
                topped with rock. Common on mid-century Phoenix homes. The asphalt goes brittle in
                the heat and the gravel hides problems until a leak shows up inside.
              </li>
              <li>
                <strong>Modified bitumen / torch-down:</strong> rolled sheets heat-welded at the
                seams. The membrane itself often holds up; the seams and flashings are what fail
                after 15–20 years of thermal cycling.
              </li>
              <li>
                <strong>TPO and single-ply:</strong> mechanically fastened or adhered sheets. UV
                eventually chalks the surface and the seams and fastener plates become the weak
                points.
              </li>
              <li>
                <strong>Rolled asphalt roofing:</strong> the cheapest flat-roof option and the
                shortest-lived in this climate. Usually well past saving by the time we see it.
              </li>
              <li>
                <strong>Metal roofing:</strong> panels and standing seam. Fastener gaskets dry out
                and leak, and expansion and contraction opens up the seams.
              </li>
            </ul>
            <p>
              <strong>Foaming over an existing roof.</strong> This is the part most homeowners
              don't know is an option. Spray foam is fully adhered, so on many built-up,
              torch-down, TPO, and metal roofs we can clean and prep the existing surface and spray
              directly over it — no tear-off, no dumpster, no exposed deck. What has to come off
              first: loose gravel, wet or saturated insulation, failed rolled roofing, and any
              structurally compromised decking. We confirm all of that during the inspection before
              anything is quoted. When a roof is a candidate, foaming over it typically costs less
              than a tear-off, adds insulation the old roof never had, and gives you a surface
              that's recoatable instead of replaceable.
            </p>
            <p>
              <strong>Sometimes we take a core sample.</strong> On older flat roofs, we may cut a
              small sample to see exactly how many roofing layers are underneath and what materials
              were previously installed. That helps us evaluate weight, moisture, tear-off costs
              and — most importantly — whether the existing roof is a good enough foundation to foam
              over.
            </p>
          </>
        ),
        },
        ...(SEARCH_TERM_SECTIONS["roof-replacement-phoenix"] ?? []),
      ]}
      process={{
        heading: "Our roof replacement process",
        steps: [
          {
            title: "Free on-site inspection",
            body: "We get on the roof, check the deck and existing system for moisture, photograph what we find, and tell you honestly whether you're looking at a repair or a replacement.",
          },
          {
            title: "Scope and written estimate",
            body: "You get a line-item estimate: system, thickness or product, warranty length, what's being removed, and what it costs. No verbal numbers, no pressure.",
          },
          {
            title: "Tear-off or prep",
            body: "Full tear-off where the old system has to go, or clean, dry, and prep where we're foaming over the existing roof. Any bad decking is repaired before we build.",
          },
          {
            title: "Install",
            body: "Foam sprayed to spec and coated, or underlayment and tile/shingle installed to manufacturer pattern. Penetrations, parapets, and drainage details handled properly — that's where roofs actually leak.",
          },
          {
            title: "Walkthrough, cleanup, and warranty",
            body: "We walk and inspect the finished roof to make sure it's complete and ready for warranty, leave the property clean, and hand off a beautiful new roof.",
          },
        ],
      }}
      whyChoose={{
        heading: "What a replacement costs",
        body: (
          <>
            <p>
              Every roof is priced off square footage, the system you choose, and what we find
              underneath. Real Phoenix ranges are published here:
            </p>
            <ul>
              <li>
                <Link to="/foam-roof-replacement-cost-phoenix">Foam roof replacement cost</Link>
              </li>
              <li>
                <Link to="/tile-roof-replacement-cost-in-phoenix">
                  Tile roof replacement / R&amp;R cost
                </Link>
              </li>
              <li>
                <Link to="/shingle-roof-cost-phoenix">Shingle roof cost</Link>
              </li>
              <li>
                <Link to="/roofing-costs-phoenix">All Phoenix roofing costs</Link>
              </li>
            </ul>
            <p>
              We can also <Link to="/financing">talk through solutions</Link> if you'd rather spread
              the project out. The exact number always comes from the on-site inspection — we don't
              quote roofs from satellite images.
            </p>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Not sure if your roof needs replacing?"
      ctaBody="Schedule a free on-site inspection. We'll tell you what shape the roof is actually in, what your options are, and what each one costs — in writing."
    />
  );
}
